import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CreditFacilityService } from './credit-facility.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'gtw-get-console-pdf',
  templateUrl: './get-console-pdf.component.html',
  styleUrls: ['./get-console-pdf.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GetConsolePdfComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  minimise = false;
  crudLoading = false;
  modalTitle = 'Generate Entity Level PDF';
  isGenerate = true;
  rowData: any = {};
  colData: any = {};
  gridData: any = {};
  filterParams: any = {};
  globalService: any = {};
  globalParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};

  showConfirmDialog = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Please Confirm',
    message: 'Are you sure you want to generate the Entity Level PDF?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  };

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      if (baseUrls) {
        const parsed = JSON.parse(baseUrls);
        this.GENERAL_CONFIG.base_url = parsed.base_url;
        if (parsed.bulkPdfEngine || parsed.bulk_pdf_engine_api_url) {
          this.GENERAL_CONFIG.bulkPdfEngine = parsed.bulkPdfEngine || parsed.bulk_pdf_engine_api_url;
          this.GENERAL_CONFIG.bulk_pdf_engine_api_url = parsed.bulk_pdf_engine_api_url || parsed.bulkPdfEngine;
        }
      }
    } catch (e) {
      // Base URLs input not yet stable
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      const data = JSON.parse(headerData);
      if (Object.keys(data).length > 0) {
        this.globalService = data.globalParams || {};
        this.globalParams = data.globalParams || {};
        this.GENERAL_CONFIG = { ...this.GENERAL_CONFIG, ...(data.generalConfigData || {}) };
        this.USER_SETTINGS = data.userSettingsData || {};
      }
    } catch (e) {
      // Header data input not yet stable
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      let data = JSON.parse(rowData);
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (innerErr) {
          // keep original parsed string when it is not JSON
        }
      }
      if (Array.isArray(data) && data.length > 0) {
        if (data[0] && data[0].data) {
          this.rowData = data[0].data || {};
        } else {
          this.rowData = data[0] || {};
        }
      } else if (data && data.data && typeof data.data === 'object') {
        this.rowData = data.data;
      } else {
        this.rowData = data || {};
      }
      this.init();
    } catch (e) {
      // Row data input not yet stable
    }
  }

  @Input('col-data')
  set _col_data(colData: string) {
    try {
      const data = JSON.parse(colData);
      this.colData = data || {};
    } catch (e) {
      // Col data input not yet stable
    }
  }

  @Input('grid-data')
  set _grid_data(gridData: string) {
    try {
      const data = JSON.parse(gridData);
      this.gridData = data || {};
    } catch (e) {
      // Grid data input not yet stable
    }
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      const params = JSON.parse(filterParams);
      if (Object.keys(params).length > 0) {
        this.filterParams = params;
        this.init();
      }
    } catch (e) {
      // Filter params input not yet stable
    }
  }

  constructor(
    private creditFacilityService: CreditFacilityService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.init();
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  init(): void {
    try {
      const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
      const hasRowData = this.rowData && Object.keys(this.rowData).length > 0;
      if (hasFilterParams && hasRowData) {
        // inputs stabilized
      }
    } catch (e) {
      console.error('Error in GetConsolePdf init:', e);
    }
  }

  confirmGenerate(): void {
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirmGenerate(): void {
    this.showConfirmDialog = false;
    this.generateConsolePdf();
  }

  onCancelConfirm(): void {
    this.cancel();
  }

  private buildPdfPayload(): {
    taxYear: string;
    scenario: string;
    jcdKey: string;
    leType: string;
    selectedView: string | null;
    form: string;
  } {
    const fp = this.filterParams || {};
    const gp = this.globalParams || {};
    return {
      taxYear: fp.tax_year || gp.tax_year || '',
      scenario: fp.scenario || gp.scenario || '',
      jcdKey: fp.jcd_key || gp.jcd_key || '',
      leType: 'CONSOL',
      selectedView: null,
      form: ''
    };
  }

  generateConsolePdf(): void {
    this.crudLoading = true;
    const pdfEngineUrl = this.GENERAL_CONFIG.bulkPdfEngine
      || this.GENERAL_CONFIG.bulk_pdf_engine_api_url
      || '/bulk-pdf-engine';
    const payload = this.buildPdfPayload();

    this.creditFacilityService.generatePdf(pdfEngineUrl, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        const errorMessage = this.creditFacilityService.getPdfErrorMessage(response);
        if (errorMessage) {
          this._snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cancel();
        } else {
          this._snackBar.open(
            this.creditFacilityService.getPdfSuccessMessage(
              response,
              'Entity Level PDF generation started successfully.'
            ),
            'Close',
            {
              duration: 4000,
              panelClass: ['success-snackbar']
            }
          );
          this.requestGridUpdate.emit({ action: 'generate' });
        }
      },
      (error: any) => {
        this.crudLoading = false;
        console.error('Error generating Entity Level PDF:', error);
        this._snackBar.open(
          this.creditFacilityService.getPdfHttpErrorMessage(
            error,
            'Error occurred while generating Entity Level PDF. Please try again.'
          ),
          'Close',
          {
            duration: 4000,
            panelClass: ['error-snackbar']
          }
        );
        this.cancel();
      }
    );
  }

  cancel(): void {
    this.crudLoading = false;
    this.requestGridUpdate.emit('cancel');
  }
}
